import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../App';
import { calcLogTime, calcSimTime, today } from '../utils/helpers';

export default function LogSession({ onSaved, editData, onCancel }) {
  const { user } = useAuth();
  const isEdit = !!editData;
  const isAdmin = user?.role === 'admin';

  const [instructors,   setInstructors]   = useState([]);
  const [students,      setStudents]      = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [busy,    setBusy]    = useState(false);

  const blank = {
    instructor_id: '',
    pic_type: '', pic_ref_id: '',
    sic_type: '', sic_ref_id: '',
    single_pilot: false,
    date: today(),
    start_time: '', end_time: '', lab_time: '',
    hobbs_start: '', hobbs_stop: '', sim_time: '',
    training_type: '', notes: '',
    squawk: '', squawk_status: 'open', squawk_progress_notes: '', squawk_resolution: ''
  };

  const [form, setForm] = useState(editData || blank);

  useEffect(() => {
    api.instructors.list().then(setInstructors).catch(() => {});
    api.students.list().then(setStudents).catch(() => {});
    api.trainingTypes.list().then(setTrainingTypes).catch(() => {});
    if (!isEdit) {
      api.sessions.lastHobbs().then(d => {
        if (d?.hobbs_stop != null) setForm(f => ({ ...f, hobbs_start: d.hobbs_stop }));
      }).catch(() => {});
    }
  }, []);

  function set(field, value) {
    setForm(f => {
      const next = { ...f, [field]: value };
      if (field === 'start_time' || field === 'end_time') {
        const lt = calcLogTime(next.start_time, next.end_time);
        if (lt > 0) next.lab_time = lt;
      }
      if (field === 'hobbs_start' || field === 'hobbs_stop') {
        next.sim_time = calcSimTime(next.hobbs_start, next.hobbs_stop);
      }
      if (field === 'training_type' && !f.notes) {
        const tt = trainingTypes.find(t => t.name === value);
        if (tt?.prefill_notes) next.notes = tt.prefill_notes;
      }
      return next;
    });
  }

  function setPilot(role, combined) {
    if (!combined) {
      setForm(f => ({ ...f, [`${role}_type`]: '', [`${role}_ref_id`]: '' }));
      return;
    }
    const [type, ref_id] = combined.split(':');
    setForm(f => ({ ...f, [`${role}_type`]: type, [`${role}_ref_id`]: ref_id }));
  }

  function picValue() {
    return form.pic_type && form.pic_ref_id ? `${form.pic_type}:${form.pic_ref_id}` : '';
  }
  function sicValue() {
    return form.sic_type && form.sic_ref_id ? `${form.sic_type}:${form.sic_ref_id}` : '';
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.instructor_id) { setError('Please select an instructor.'); return; }
    if (!form.training_type) { setError('Please select a training type.'); return; }
    if (!form.pic_type || !form.pic_ref_id) { setError('Please select a PIC.'); return; }
    setError(''); setBusy(true);
    try {
      if (isEdit) {
        await api.sessions.update(editData.id, form);
        setSuccess('Session updated.');
        setTimeout(() => { setSuccess(''); if (onSaved) onSaved(); }, 1200);
      } else {
        await api.sessions.create(form);
        setSuccess('Session logged successfully!');
        setForm(blank);
        // Re-fetch last hobbs for next entry
        api.sessions.lastHobbs().then(d => {
          if (d?.hobbs_stop != null) setForm(f => ({ ...f, hobbs_start: d.hobbs_stop }));
        }).catch(() => {});
        setTimeout(() => { setSuccess(''); if (onSaved) onSaved(); }, 1200);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function pilotOptions() {
    return (
      <>
        <optgroup label="Instructors">
          {instructors.map(i => (
            <option key={`instructor:${i.id}`} value={`instructor:${i.id}`}>
              {i.name} ({i.instructor_id})
            </option>
          ))}
        </optgroup>
        <optgroup label="Students">
          {students.map(s => (
            <option key={`student:${s.id}`} value={`student:${s.id}`}>
              {s.name} ({s.student_id})
            </option>
          ))}
        </optgroup>
      </>
    );
  }

  const squawkStatusLabel = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved' };

  return (
    <div>
      <div className="section-title">{isEdit ? 'Edit Session' : 'Log Session'}</div>

      <div className="card">
        <div className="card-header">
          <h2>{isEdit ? 'Edit Simulator Session' : 'New Simulator Session'}</h2>
          <p>All times local. Lab time auto-calculates from start/stop. Sim time auto-calculates from Hobbs.</p>
        </div>
        <div className="card-body">
          {error   && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={submit}>
            <div className="form-grid">

              {/* Row 1: Instructor + Training Type */}
              <div className="form-group">
                <label>Instructor *</label>
                <select value={form.instructor_id} onChange={e => set('instructor_id', e.target.value)} required>
                  <option value="">Select instructor...</option>
                  {instructors.map(i => (
                    <option key={i.id} value={i.id}>{i.name} ({i.instructor_id})</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Training Type *</label>
                <select value={form.training_type} onChange={e => set('training_type', e.target.value)} required>
                  <option value="">Select type...</option>
                  {trainingTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>

              {/* Row 2: PIC + SIC */}
              <div className="form-group">
                <label>PIC (Pilot in Command) *</label>
                <select value={picValue()} onChange={e => setPilot('pic', e.target.value)} required>
                  <option value="">Select PIC...</option>
                  {pilotOptions()}
                </select>
              </div>

              <div className="form-group">
                <label>SIC (Second in Command)</label>
                <select value={sicValue()} onChange={e => setPilot('sic', e.target.value)}>
                  <option value="">Select SIC (optional)...</option>
                  {pilotOptions()}
                </select>
              </div>

              {/* Row 3: Date + Single Pilot */}
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
              </div>

              <div className="form-group" style={{display:'flex', alignItems:'center', paddingTop:24}}>
                <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:8, fontWeight:500}}>
                  <input
                    type="checkbox"
                    checked={form.single_pilot}
                    onChange={e => set('single_pilot', e.target.checked)}
                  />
                  Single Pilot Operation
                </label>
              </div>

              {/* Lab Time section header */}
              <div className="form-group full">
                <div className="time-section-label">Lab Time</div>
              </div>

              {/* Lab Start | Lab Stop — same grid as all other 2-col rows */}
              <div className="form-group">
                <label>Lab Start *</label>
                <input type="time" lang="en-GB" value={form.start_time} onChange={e => set('start_time', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Lab Stop *</label>
                <input type="time" lang="en-GB" value={form.end_time} onChange={e => set('end_time', e.target.value)} required />
              </div>
              <div className="form-group full">
                <label>Lab Time (tenths) — Auto-calculated</label>
                <input type="number" step="0.1" min="0" max="24" value={form.lab_time} onChange={e => set('lab_time', e.target.value)} required />
              </div>

              {/* Hobbs / Sim Time section header */}
              <div className="form-group full">
                <div className="time-section-label">Hobbs / Sim Time</div>
              </div>

              {/* Hobbs Start | Hobbs Stop — same grid as all other 2-col rows */}
              <div className="form-group">
                <label>Hobbs Start *</label>
                <input type="number" step="0.1" min="0" value={form.hobbs_start} onChange={e => set('hobbs_start', e.target.value)} placeholder="e.g. 1234.5" required />
              </div>
              <div className="form-group">
                <label>Hobbs Stop *</label>
                <input type="number" step="0.1" min="0" value={form.hobbs_stop} onChange={e => set('hobbs_stop', e.target.value)} placeholder="e.g. 1236.0" required />
              </div>
              <div className="form-group full">
                <label>Sim Time (tenths) — Auto-calculated</label>
                <input type="number" step="0.1" min="0" max="24" value={form.sim_time} onChange={e => set('sim_time', e.target.value)} required />
              </div>

              {/* Session Notes */}
              <div className="form-group full">
                <label>Session Notes</label>
                <textarea
                  rows={3} value={form.notes}
                  placeholder="Training objectives, maneuvers practiced, performance notes..."
                  onChange={e => set('notes', e.target.value)}
                />
              </div>

              {/* Squawk */}
              <div className="form-group full">
                <label>Squawk / Maintenance Issue</label>
                <textarea
                  rows={2} value={form.squawk}
                  placeholder="Describe any equipment issues observed during the session..."
                  onChange={e => set('squawk', e.target.value)}
                />
              </div>

              {/* Squawk management — admin only, when squawk text is present */}
              {form.squawk && isAdmin && (
                <>
                  <div className="form-group">
                    <label>Squawk Status</label>
                    <select value={form.squawk_status} onChange={e => set('squawk_status', e.target.value)}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>

                  {(form.squawk_status === 'in_progress' || form.squawk_status === 'resolved') && (
                    <div className="form-group">
                      <label>Progress Notes</label>
                      <textarea
                        rows={2} value={form.squawk_progress_notes}
                        placeholder="Work performed, parts ordered, maintenance in progress..."
                        onChange={e => set('squawk_progress_notes', e.target.value)}
                      />
                    </div>
                  )}

                  {form.squawk_status === 'resolved' && (
                    <div className="form-group">
                      <label>Resolution Notes</label>
                      <textarea
                        rows={2} value={form.squawk_resolution}
                        placeholder="How was the issue resolved?"
                        onChange={e => set('squawk_resolution', e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Non-admin read-only squawk status when editing */}
              {form.squawk && !isAdmin && isEdit && form.squawk_status && (
                <div className="form-group">
                  <label>Squawk Status</label>
                  <div style={{paddingTop:6}}>
                    <span className={`badge badge-${form.squawk_status === 'open' ? 'open' : form.squawk_status === 'in_progress' ? 'progress' : 'resolved'}`}>
                      {squawkStatusLabel[form.squawk_status] || form.squawk_status}
                    </span>
                  </div>
                </div>
              )}

            </div>

            <div className="btn-row">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Saving...' : (isEdit ? 'Save Changes' : 'Log Session')}
              </button>
              {onCancel && (
                <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
